import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import { SHIPPING_TYPE } from "constants/order";
import {
  useCreateOrder,
  useDistricts,
  useListCustomerByKeySearch,
  useLocationsByCustomer,
  useProvinces,
  useTicketDetail,
  useWards,
} from "hooks";
import { CustomerLocationType } from "pages/CustomerManagement/Location/utils";
import { getInitCustomerParams } from "pages/CustomerManagement/utils";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container, Spinner } from "reactstrap";
import MessageModal from "../../../components/shared-components/MessageModal";
import OrdersFormV2 from "../OrdersForm/OrdersFormV2";

const initLocationParams = () => {
  return {
    keywords: "",
    location_type: CustomerLocationType.RECEIVE,
  };
};

const initLocalitiesParams = () => {
  return {
    countryId: 1,
    type: 1,
    parent_code: "",
  };
};

const OrderPageV2 = props => {
  const { t } = useTranslation(["order"]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketIdForOrder = Number(searchParams.get("ticketId"));
  const hasTicketContext =
    Number.isInteger(ticketIdForOrder) && ticketIdForOrder > 0;

  const {
    data: ticketDetail,
    isLoading: isLoadingTicketDetail,
    isError: isTicketDetailError,
  } = useTicketDetail(hasTicketContext ? ticketIdForOrder : null);

  const isHiddenShippingUnit = Boolean(ticketDetail?.is_hidden_shipping_unit);
  const isTicketDetailReady =
    !hasTicketContext || (!isLoadingTicketDetail && !isTicketDetailError);

  const [wardsForDisplay, setWardsForDisplay] = useState({});

  const [provinceParams, setProvinceParams] = useState(initLocalitiesParams);
  const [districtParams, setDistrictParams] = useState(initLocalitiesParams);
  const [wardParams, setWardParams] = useState(initLocalitiesParams);
  const [customerParams, setCustomerParams] = useState(getInitCustomerParams);
  const [locationParams, setLocationParams] = useState(initLocationParams);
  const [openPopup, setOpenPopup] = useState(false);
  const [orderCode, setOrderCode] = useState("");
  const [customerCode, setCustomerCode] = useState("");
  const [messagePopups, setMessagePopups] = useState([]);
  const [localitiesRecordStatus, setLocalitiesRecordStatus] = useState("O");

  const { data: customers, refetch: listCustomer } = useListCustomerByKeySearch(
    {
      params: customerParams,
    }
  );

  useEffect(() => {
    if (customerParams && customerParams.run) {
      listCustomer();
    }
  }, [customerParams, listCustomer]);

  useEffect(() => {
    setCustomerParams(prev => ({
      ...prev,
      sizePerPage: 100,
      run: true,
      shipping_type: [
        SHIPPING_TYPE.DVVC,
        SHIPPING_TYPE.NHAXE,
        SHIPPING_TYPE.RUNNER,
      ].join(","),
      columnFilter: {
        "filter[record_status]": "O",
      },
    }));
  }, []);

  const { data: provinces, refetch: refetchProvinces } =
    useProvinces(provinceParams);
  const { data: districts, refetch: refetchDistricts } =
    useDistricts(districtParams);
  const { data: wards, refetch: refetchWards } = useWards(wardParams);
  const { data: districtAll, refetch: getDistricts } = useDistricts({
    countryId: 1,
    type: 2,
    parent_code: "",
  });
  const { data: wardAll, refetch: getWards } = useWards({
    countryId: 1,
    type: 3,
    parent_code: "",
  });

  const { data: locationsCustomer, refetch: refetchLocationCustomer } =
    useLocationsByCustomer({
      customerId: locationParams?.customerId,
      params: locationParams,
    });

  const handleChangeCustomer = customerId => {
    setLocationParams(prev => ({
      ...prev,
      customerId: customerId,
    }));
  };
  const handleSelectAddress = selectedAddress => {
    if (
      !selectedAddress ||
      typeof selectedAddress.is_new_address === "undefined"
    ) {
      setLocalitiesRecordStatus("O");
      return;
    }

    const newStatus = selectedAddress.is_new_address === "O" ? "C" : "O";
    setLocalitiesRecordStatus(newStatus);
  };
  const onSuccessCreateOrder = _data => {
    console.log("🚀 ~ onSuccessCreateOrder ~ _data:", _data);
    if (
      _data.shipping_type === SHIPPING_TYPE.NHAXE ||
      customerCode === "GSNEW"
    ) {
      setOrderCode(_data.order_code);
      const mes = [];
      if (_data.shipping_type === SHIPPING_TYPE.NHAXE) {
        mes.push({
          value: t("message.note", {
            order_code: _data.order_code,
          }),
          class: "",
        });
      }
      if (customerCode === "GSNEW") {
        mes.push({
          value: t("message.note_create_GSNEW"),
          class: "text-danger",
        });
      }
      setMessagePopups(mes);
      setOpenPopup(true);
    } else if (_data.shipping_type === SHIPPING_TYPE.DVVC) {
      const mes = [];
      mes.push({
        value: t("message.note_shipping_type"),
        class: "",
      });
      setMessagePopups(mes);
      setOpenPopup(true);
    } else {
      navigate("/manage-orders");
    }
  };

  const onErrorCreateOrder = _data => { };

  const { mutate: createOrder, isLoading: isCreateOrder } = useCreateOrder(
    onSuccessCreateOrder,
    onErrorCreateOrder
  );

  const handleCreateOrder = data => {
    setOrderCode("");
    setCustomerCode(data.customer_code);
    createOrder({
      ...data,
      ticket_id: hasTicketContext ? ticketIdForOrder : undefined,
    });
  };

  useEffect(() => {
    const { customerId } = locationParams;
    if (customerId) {
      refetchLocationCustomer();
    }
  }, [locationParams, refetchLocationCustomer]);

  useEffect(() => {
    refetchProvinces();
    getDistricts();
    getWards();
  }, []);

  useEffect(() => {
    if (provinceParams?.type === 1) refetchProvinces();
  }, [provinceParams, refetchProvinces]);

  useEffect(() => {
    if (districtParams?.type === 2) refetchDistricts();
  }, [districtParams, refetchDistricts]);

  useEffect(() => {
    setProvinceParams(prev => ({
      ...prev,
      record_status: localitiesRecordStatus,
    }));
    setDistrictParams(prev => ({
      ...prev,
      record_status: localitiesRecordStatus,
    }));
    setWardParams(prev => ({ ...prev, record_status: localitiesRecordStatus }));
  }, [localitiesRecordStatus]);

  useEffect(() => {
    if (wardParams?.type === 3) refetchWards();
  }, [wardParams, refetchWards]);

  const toggleModalNotify = () => {
    setOpenPopup(!openPopup);
    handleCancel();
  };

  const handleCancel = () => {
    navigate("/manage-orders");
  };
  const handleLocationKeywordsChange = value => {
    setLocationParams(prev => ({ ...prev, keywords: value }));
  };
  const handleCustomerKeywordsChange = value => {
    setCustomerParams(prev => ({ ...prev, keywords: value }));
  };
  useEffect(() => {
    if (districts) {
      setWardsForDisplay(districts);
    }
  }, [districts]);

  return (
    <React.Fragment>
      <div className="page-content">
        <MessageModal
          isOpen={openPopup}
          autoFocus={true}
          centered={true}
          toggle={toggleModalNotify}
          clickOutsideNotClose={true}
          type={"infor"}
          message={t("message.note", {
            order_code: orderCode,
          })}
          messages={messagePopups}
        ></MessageModal>
        <MetaTags>
          <title>{t("title.create")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.create")}
            breadcrumbItems={[
              { name: t("title.manager") },
              { name: t("title.list"), link: "/manage-orders" },
              { name: t("title.create") },
            ]}
          />
        </div>
        <Container fluid={true}>
          {!isTicketDetailReady ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <OrdersFormV2
              isHiddenShippingUnit={isHiddenShippingUnit}
              customers={customers?.items}
              locationsCustomer={locationsCustomer?.items}
              handleChangeCustomer={handleChangeCustomer}
              provinces={provinces}
              districts={districts}
              wards={wardsForDisplay}
              districtAll={districtAll}
              wardAll={wardAll}
              createOrder={handleCreateOrder}
              onChangeProvince={value => {
                setDistrictParams(prev => ({
                  ...prev,
                  type: 2,
                  parent_code: value,
                }));
              }}
              // onChangeDistrict={value => {
              //   setWardParams(prev => ({ ...prev, type: 3, parent_code: value }));
              // }}
              onKeywordsChange={handleCustomerKeywordsChange}
              refetchLocationCustomer={refetchLocationCustomer}
              handleCancel={handleCancel}
              onLocationKeywordsChange={handleLocationKeywordsChange}
              isCreateOrder={isCreateOrder}
              onSelectAddress={handleSelectAddress}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OrderPageV2;
